import * as React from "react";
import {inject} from "mobx-react";
import {ClickToEditInput} from "../../../widgets/input/clicktoedit/ClickToEditInput";
import {OptionsButton} from "../../../widgets/button/options/OptionsButton";
import type {IContextOption} from "../../../widgets/context/ContextOptions";
import {ReactUtils} from "../../../utils/ReactUtils";
import {MathUtils} from "../../../../utils/math/MathUtils";
import type {AppState} from "../../../../data/state/AppState";
import {XyiconFeature} from "../../../../generated/api/base";
import type {Space} from "../../../../data/models/Space";
import type {SpaceVersion} from "../../../../data/models/SpaceVersion";
import {DateFormatter} from "../../../../utils/format/DateFormatter";
import {Functions} from "../../../../utils/function/Functions";
import {colorPalette} from "../../../5.0/styles/colorPalette";
import {fontSize, fontWeight} from "../../../5.0/styles/styles";
import {isSpaceNamePresentInAnotherSpaceVersion} from "../spaceeditor/logic3d/renderers/SpaceViewRendererUtils";
import type {IOnePagePDFDoc} from "./CreateSpacePanel";

export type PDFListItemMode = "EditSpaceNames" | "SetScaleOrAlign" | "Confirm";

interface IPDFListItemProps {
	readonly appState?: AppState;
	readonly selectedVersion: SpaceVersion;
	readonly onePagePDFDoc: IOnePagePDFDoc;
	readonly mode: PDFListItemMode;
	readonly active: boolean;
	readonly originalPDFIndex: number;
	readonly originalPageNumber: number;
	readonly originalPDFFileName: string;
	readonly forceUpdate: () => void;
	readonly onSpaceNameChange: (originalPDFIndex: number, originalPageNumber: number, newName: string) => void;
	readonly isSpaceNameAvailable: (spaceName: string) => boolean;
	readonly onRotate: (originalPDFIndex: number, originalPageNumber: number, deltaAngle: number) => void;
	readonly onDeleteClick: (originalPDFIndex: number, originalPageNumber: number) => void;
	readonly onSelect: (originalPDFIndex: number, originalPageNumber: number) => void;
}

interface IPDFListItemState {
	thumbnailURL: string;
	isWaitingOnThumbnail: boolean;
}

@inject("appState")
export class PDFListItem extends React.Component<IPDFListItemProps, IPDFListItemState> {
	private _originalRotation: number;
	private _rotation: number;

	private _isMounted: boolean = false;

	constructor(props: IPDFListItemProps) {
		super(props);

		this.state = {
			thumbnailURL: "",
			isWaitingOnThumbnail: true,
		};
	}

	private onClick = () => {
		if (this.props.onSelect) {
			this.props.onSelect(this.props.originalPDFIndex, this.props.originalPageNumber);
		}
	};

	private onRotateCWClick = () => {
		this.props.onRotate(this.props.originalPDFIndex, this.props.originalPageNumber, 90);
		this.updateThumbnail();
	};

	private onRotateCCWClick = () => {
		this.props.onRotate(this.props.originalPDFIndex, this.props.originalPageNumber, -90);
		this.updateThumbnail();
	};

	private async updateThumbnail() {
		if (this._isMounted) {
			this.setState({
				isWaitingOnThumbnail: true,
			});
			this.props.forceUpdate();

			this._originalRotation = this.props.onePagePDFDoc.originalRotation;
			const pdf = this.props.onePagePDFDoc.doc;
			const firstPage = pdf.getPages()[0];

			this._rotation = MathUtils.clampDegreeBetween0And360(firstPage.getRotation().angle);

			const deltaAngle = MathUtils.clampDegreeBetween0And360(this._rotation - this._originalRotation);

			const thumbnailObject = await this.props.appState.app.graphicalTools.pdfRenderer.getThumbnailAndViewBox(
				400,
				this.props.onePagePDFDoc.doc,
				`${this.props.originalPDFFileName}_${this.props.originalPageNumber}`,
				deltaAngle,
			);

			if (this._isMounted) {
				if (!this.props.onePagePDFDoc.thumbnail || !this.props.onePagePDFDoc.insertionInfo) {
					if (!this.props.onePagePDFDoc.thumbnail) {
						this.props.onePagePDFDoc.thumbnail = thumbnailObject.url;
					}
					if (!this.props.onePagePDFDoc.insertionInfo) {
						this.props.onePagePDFDoc.insertionInfo = thumbnailObject.insertionInfo;
					}
					this.props.forceUpdate();
				}

				this.setState({
					thumbnailURL: thumbnailObject.url,
					isWaitingOnThumbnail: false,
				});
			}
		}
	}

	private getVersionHistory() {
		const {actions} = this.props.appState;
		const spaces = actions.getList<Space>(XyiconFeature.Space);
		const spaceWithTheSameName = spaces.find((space: Space) => space.name === this.props.onePagePDFDoc.spaceName);

		const spaceVersions = [this.props.selectedVersion];

		if (spaceWithTheSameName) {
			const relatedSpaceVersions = spaceWithTheSameName.versions;

			this.props.onePagePDFDoc.spaceId = spaceWithTheSameName.id;
			this.props.onePagePDFDoc.originalInsertionInfo = this.props.onePagePDFDoc.insertionInfo;
			this.props.onePagePDFDoc.insertionInfo = {...this.props.onePagePDFDoc.insertionInfo, ...spaceWithTheSameName.newestSpaceFile.insertionInfo};

			for (const spaceVersion of relatedSpaceVersions) {
				if (!spaceVersions.includes(spaceVersion)) {
					spaceVersions.push(spaceVersion);
				}
			}
		} else {
			this.props.onePagePDFDoc.spaceId = null;
			this.props.onePagePDFDoc.insertionInfo = this.props.onePagePDFDoc.originalInsertionInfo ?? this.props.onePagePDFDoc.insertionInfo;
		}

		return spaceVersions.map((spaceVersion: SpaceVersion) => {
			return (
				<div
					className="vbox flexCenter"
					key={spaceVersion.id}
				>
					<div>{spaceVersion.name}</div>
					<div className="darkSilverText">{DateFormatter.format(spaceVersion.date.toString())}</div>
				</div>
			);
		});
	}

	private onSpaceNameChange = (newName: string) => {
		const errorMessage = this.getErrorMessage(newName);
		if (!errorMessage && newName !== this.props.onePagePDFDoc.spaceName) {
			this.props.onSpaceNameChange(this.props.originalPDFIndex, this.props.originalPageNumber, newName);
		}
	};

	private getErrorMessage = (value: string) => {
		if (value === this.props.onePagePDFDoc.spaceName) {
			return ""; // No 'unique error' if input the value is the same as the current name
		}
		return !value.trim() ? "Name cannot be empty!" : this.props.isSpaceNameAvailable(value) ? "" : "Name needs to be unique!";
	};

	public override componentDidMount() {
		this._isMounted = true;
		this.updateThumbnail();
	}

	public override componentWillUnmount() {
		this._isMounted = false;
	}

	public override render() {
		const options: IContextOption[] = [
			{
				label: "Rotate 90° CW",
				onSelect: this.onRotateCWClick,
			},
			{
				label: "Rotate 90° CCW",
				onSelect: this.onRotateCCWClick,
			},
			{
				label: "Delete",
				onSelect: () => {
					this.props.onDeleteClick(this.props.originalPDFIndex, this.props.originalPageNumber);
				},
			},
		];

		const requiresAlignment =
			this.props.mode === "SetScaleOrAlign" &&
			isSpaceNamePresentInAnotherSpaceVersion(this.props.appState.actions, this.props.onePagePDFDoc.spaceName);
		const isScaleSet =
			this.props.mode === "SetScaleOrAlign" && !requiresAlignment && MathUtils.isValidNumber(this.props.onePagePDFDoc.spaceUnitsPerMeter);
		const isAlignConfirmed = this.props.mode === "SetScaleOrAlign" && requiresAlignment && this.props.onePagePDFDoc.isAlignmentConfirmed;
		const isSkipped = this.props.mode === "SetScaleOrAlign" && this.props.onePagePDFDoc.isSkipped;

		return (
			<div
				className={ReactUtils.cls("pdfContainer hbox bordered", {
					disabled: this.state.isWaitingOnThumbnail,
					active: this.props.active,
					yellow: isSkipped,
					green: isScaleSet || isAlignConfirmed,
				})}
				onClick={this.onClick}
			>
				<div className="hbox flex1">
					<div
						style={{backgroundImage: `url("${this.state.thumbnailURL}")`}}
						className="thumbnail"
					>
						{this.state.isWaitingOnThumbnail && <span className="spinner visible" />}
					</div>
					<div className="vbox justifyCenter">
						{this.props.mode === "EditSpaceNames" ? (
							<>
								<div className="bold">{`Page ${this.props.onePagePDFDoc.originalPageNumber + 1}`}</div>
								<div className="darkSilverText">{this.props.originalPDFFileName}</div>
							</>
						) : (
							<>
								<div className="bold">{this.props.onePagePDFDoc.spaceName}</div>
								{isSkipped && (
									<div style={{fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: `${colorPalette.warning.c500Primary}`}}>Skipped</div>
								)}
							</>
						)}
					</div>
				</div>
				{this.props.mode === "EditSpaceNames" && (
					<>
						<ClickToEditInput
							value={this.props.onePagePDFDoc.spaceName}
							className="flexCenter flex1 spaceName center"
							onChange={this.onSpaceNameChange}
							onBlur={Functions.emptyFunction}
							getErrorMessage={this.getErrorMessage}
							noButtons={true}
						/>
						<div className="vbox flexCenter flex1">{this.getVersionHistory()}</div>

						<OptionsButton
							className="options flexCenter"
							options={options}
						/>
					</>
				)}
			</div>
		);
	}
}
